import React, { useEffect, useState, useRef } from "react";

import * as notify from "shared/notify";

import { useMeasurements } from "components/shipment-editor/measurements/hooks/use-measurements";
import RequestMeasurements from "./components/request-measurements";
import MeasurementSelector from "./components/measurement-selector";
import Tabs from "../tabs";
import { getTaskContextByKey, getTasksByContextKey } from "components/task-list/lib/taskData";

import { validateMeasurements } from "./lib/helpers";
import { SourcePage } from "shared/taskTypeMap";
import eventEmitter from "shared/events";
import { completeTask } from "shared/taskState";
import { usePositions } from "./hooks/api/use-positions";
import MeasurementTab from "./components/measurement-tab";
import Attributes from "contexts/attributes/attributes";
import { getMeasurementData, validateMeasurementTask } from "./lib/measuring-data";
import { isEmpty } from "lodash";

import "./measuring.scss";

const MeasurementsInput = ({ shipmentData, editOnly, sourcePage, disableSave, measurementsFromPhotography, ...props }) => {
    const {
        selectedUpcs,
        singleHighlightedCodingWipKey,
        singleHighlightedMeasuringWipKey,
        updatePosition,
        initializeMeasurementsContainer,
        measurementState,
        setMeasurementState,
        saveMeasurement,
        addPosition,
        removePosition,
        setInvalidMeasurements,
    } = useMeasurements();
    const [wipKeys, setWipKeys] = useState([]);
    const [shipmentInfo, setShipmentInfo] = useState([]);
    const saveButtonRef = useRef();
    const isCompleted = measurementState.isCompleted;
    const [selectedTab, setSelectedTab] = useState(0);
    const { data: positionListData, isSuccess } = usePositions();
    const positionList = measurementState.positionList;
    const measurements = measurementState.measurements;
    const currentPosition = measurementState.currentPosition;
    const isDigitalLite = measurementState.isDigitalLite;
    const invalidMeasurements = measurementState.invalidMeasurements;
    const [codingWipKey, setCodingWipKey] = useState("");
    const [selectedCodingWipKeys, setSelectedCodingWipKeys] = useState([]);
    const standardMeasuringPosition = positionList?.all?.find(({ value }) => value === "standard");

    const updateMeasuringTable = async () => {
        const data = await getTaskContextByKey(shipmentData.key);

        if (data.length === 0) {
            setCodingWipKey("");

            return;
        }

        setShipmentInfo(data);
    };

    const getCodingWipKey = () => {
        if (singleHighlightedCodingWipKey) {
            setSelectedCodingWipKeys([]);

            return setCodingWipKey(singleHighlightedCodingWipKey);
        }
    };

    useEffect(() => {
        eventEmitter.on("ADD_DELETE_UPC", updateMeasuringTable);
        eventEmitter.on("NOTE_ADDED", updateMeasuringTable);

        return () => {
            eventEmitter.removeListener("ADD_DELETE_UPC", updateMeasuringTable);
            eventEmitter.removeListener("NOTE_ADDED", updateMeasuringTable);
        };
    }, []);

    useEffect(() => {
        updateMeasuringTable();
    }, []);

    useEffect(() => {
        if (shipmentData && positionListData && isSuccess) {
            setMeasurementState((current) => ({
                ...current,
                isDigitalLite: shipmentData.shipmentType === 2,
            }));
        }
    }, [shipmentData, positionListData, isSuccess]);

    useEffect(() => {
        if (!measurementState.measuringWipKey) {
            return;
        }

        getCodingWipKey();
        setWipKeys([measurementState.measuringWipKey]);
    }, [singleHighlightedCodingWipKey]);

    useEffect(() => {
        if (selectedUpcs?.length > 0) {
            let measurementWipKeys = [];
            let codingKeys = [];
            selectedUpcs.forEach((upc) => {
                measurementWipKeys.push(upc.measuringWipKey);
                codingKeys.push(upc.codingWipKey);
            });
            setCodingWipKey(codingKeys[0]);
            setSelectedCodingWipKeys(codingKeys);
            setWipKeys(measurementWipKeys);
        } else {
            if (!singleHighlightedCodingWipKey && shipmentInfo.length > 0) {
                setCodingWipKey(shipmentInfo[0].codingWipKey);
                setWipKeys([]);
            }
        }
    }, [selectedUpcs, shipmentInfo, singleHighlightedCodingWipKey]);

    useEffect(() => {
        // Reset invalid fields
        const invalidFieldsToReset = document.querySelectorAll(":invalid");
        [...invalidFieldsToReset].map((f) => f.setCustomValidity(""));

        // Run through each invalid measurement and set the validity
        if (invalidMeasurements.length > 0) {
            const invalidFields = invalidMeasurements.map((m) => m.errors);
            const invalidMessages = invalidMeasurements
                .map((m) => m.message)
                .join(" ")
                .trim();
            const preMessage = invalidMeasurements[0].preMessage;
            invalidMeasurements.forEach((m) => {
                m.errors.forEach((f) => {
                    document.querySelector(`#measurement_line_${m.key.join("-")} #x${m.key.join("-")}_${f}`).setCustomValidity("Invalid field.");
                });
            });

            notify.error(
                `${preMessage ? preMessage : "Invalid measurements: "} ${
                    invalidMessages && invalidMessages.length > 0 ? invalidMessages : invalidFields.join(", ")
                }`
            );
        }
    }, [invalidMeasurements]);

    useEffect(() => {
        if ((wipKeys && isSuccess) || measurementsFromPhotography) {
            initializeMeasurementsContainer(measurementsFromPhotography);
        }
    }, [wipKeys, isSuccess, measurementsFromPhotography]);

    useEffect(() => {
        if (isEmpty(positionList.active)) {
            return;
        }

        if (selectedTab === -1 || selectedTab >= positionList.active.length) {
            const lastIndex = positionList.active.length - 1;
            setSelectedTab(lastIndex);

            return updatePosition(positionList.active[lastIndex]);
        }

        return updatePosition(positionList.active[selectedTab]);
    }, [positionList, selectedTab]);

    // QA Nav Functions
    const validate = async (taskContextKey) => {
        //Bypass validation if Digital Lite
        if (isDigitalLite) {
            return true;
        }

        let isValid = false;

        try {
            isValid = validateMeasurements(positionList.active, measurements, shipmentData.hasMetricMeasurementsOnly, false, setInvalidMeasurements);
        } catch (ex) {
            notify.error(ex.message);

            return false;
        }

        // Can't use the attributeValidity custom hook anymore because every position's validation is different
        const inputs = document.querySelectorAll(".measuring-attributes input, .measuring-attributes select");
        const anyInvalid = [...inputs].some((i) => i.validity.valid === false);

        if (anyInvalid) {
            const invalidInputs = [...inputs].filter((i) => i.validity.valid === false);
            const $parent = invalidInputs[0].closest(".measuring-attributes");

            if (!$parent) {
                notify.error(`Required attributes need attention.`);
            }

            const position = $parent.dataset.position;
            notify.error(`Required attributes need attention in ${position}.`);

            return false;
        }

        if (!isValid) {
            return false;
        }

        const taskKeys = await getTasksByContextKey(taskContextKey, shipmentData.isInDuplicateStaging).then((tasks) => {
            return tasks
                .filter((task) => task.taskType.split(".")[0] === "measuring")
                .sort((a, b) => new Date(b.taskCreatedOn) - new Date(a.taskCreatedOn));
        });

        const taskKey = taskKeys[0].key;

        return await validateMeasurementTask(taskKey);
    };

    // Tabs
    const tabContent = positionList.active.map((position, i) => {
        if (codingWipKey || selectedCodingWipKeys.length > 0) {
            return (
                <div key={i}>
                    <Attributes
                        useTask={{
                            task: {
                                key: shipmentData.key,
                                context: {
                                    codingWipKey: codingWipKey,
                                    codingWipKeys: selectedCodingWipKeys,
                                    sourcePage: SourcePage["shipment.standard"],
                                },
                            },
                        }}
                    >
                        <MeasurementTab
                            key={`position_${position.id}`}
                            isSelected={selectedTab === i}
                            position={position}
                            wipKeys={wipKeys}
                            codingWipKey={codingWipKey}
                            measurements={measurements}
                            shipmentKey={shipmentData.key}
                            saveMeasurement={saveMeasurement}
                            editOnly={false}
                        />
                    </Attributes>
                </div>
            );
        }

        return null;
    });
    const addTab = (position) => {
        addPosition(position);

        return setSelectedTab(positionList.active.length);
    };
    const removeTab = async (positionIndex) => {
        try {
            await removePosition(measurementState, positionList.active[positionIndex], measurements);

            return setSelectedTab(-1);
        } catch (ex) {
            notify.error(ex.message);
        }
    };

    const getAllTasks = async (key) => {
        return await getTasksByContextKey(key, shipmentData.isInDuplicateStaging);
    };

    const getTaskKeys = async () => {
        let taskContextKeys = [];
        let ids = [];

        let data = await getMeasurementData(shipmentData.key);
        data.map((d) => (wipKeys.includes(d.measuringWipKey) ? taskContextKeys.push(d.taskContextKey) : null));

        if (taskContextKeys.length === 0 && singleHighlightedMeasuringWipKey) {
            const singleSelectedRedoProduct = data.find((upc) => upc.measuringWipKey === singleHighlightedMeasuringWipKey);
            taskContextKeys.push(singleSelectedRedoProduct.taskContextKey);
        }

        for (let key of taskContextKeys) {
            const valid = await validate(key);

            if (valid) {
                const taskKeys = await getAllTasks(key).then((tasks) => {
                    return tasks
                        .filter((task) => task.taskType.split(".")[0] === "measuring")
                        .sort((a, b) => new Date(b.taskCreatedOn) - new Date(a.taskCreatedOn));
                });

                ids.push(taskKeys[0].key);
            }
        }

        return ids;
    };

    const upcNotSelected = wipKeys.length === 0 && !singleHighlightedCodingWipKey;

    const save = async () => {
        if (upcNotSelected) {
            notify.error("UPC selection is required when adding measurements.");

            return;
        }

        const taskKeys = await getTaskKeys();

        if (taskKeys.length === 0) {
            return notify.error("Unable to save measurements");
        }

        for (let id of taskKeys) {
            await completeTask(id, false, false);
            notify.success("Saved!");
        }

        return eventEmitter.emit("REFRESH_TASK_STATUS");
    };

    // use only for stand measurements update AND inside attributes context
    if (editOnly && standardMeasuringPosition && (codingWipKey || selectedCodingWipKeys.length > 0)) {
        if (props.showTabs && props.activePositions) {
            const tabEdits = props.activePositions.map((position, i) => {
                return (
                    <div
                        className="measurements"
                        key={`${position}_${i}`}
                    >
                        <div className="column -span12">
                            <MeasurementTab
                                sourcePage={sourcePage}
                                editOnly={editOnly}
                                isSelected
                                position={position}
                                wipKeys={wipKeys}
                                singleHighlightedCodingWipKey={singleHighlightedCodingWipKey}
                                codingWipKey={codingWipKey}
                                measurements={measurements}
                                shipmentKey={shipmentData.key}
                                saveMeasurement={saveMeasurement}
                                renderAttributes={false}
                            />
                        </div>
                    </div>
                );
            });

            return (
                <Tabs
                    reportSelectedTab={(tabIndex) => {
                        props.setSelectedTabOverride(tabIndex);
                    }}
                    readOnly={true}
                    selectedTab={props.selectedTabOverride}
                    tabs={props.activePositions}
                    tabClassOverride={"-primary -no-focus"}
                >
                    {tabEdits}
                </Tabs>
            );
        } else {
            return (
                <div className="measurements">
                    <div className="column -span12">
                        <MeasurementTab
                            sourcePage={sourcePage}
                            editOnly={editOnly}
                            isSelected
                            position={standardMeasuringPosition}
                            wipKeys={wipKeys}
                            singleHighlightedCodingWipKey={singleHighlightedCodingWipKey}
                            codingWipKey={codingWipKey}
                            measurements={measurements}
                            shipmentKey={shipmentData.key}
                            saveMeasurement={saveMeasurement}
                        />
                    </div>
                </div>
            );
        }
    }

    return (
        <>
            {isDigitalLite && (
                <div className="home container txt-center">
                    <h1>Digital Lite</h1>
                </div>
            )}
            {!isDigitalLite && (
                <div>
                    <div className="measurements">
                        <div className="column -span12">
                            <div className="request-measurements-container">
                                <RequestMeasurements
                                    currentPosition={currentPosition}
                                    measurementState={measurementState}
                                    saveMeasurement={saveMeasurement}
                                    isCompleted={isCompleted}
                                    upcNotSelected={upcNotSelected}
                                />
                            </div>
                            <Tabs
                                addTab={addTab}
                                reportSelectedTab={(tabIndex) => {
                                    setSelectedTab(tabIndex);
                                }}
                                readOnly={isCompleted}
                                selectedTab={selectedTab}
                                tabs={positionList.active}
                                TabDropdown={MeasurementSelector(positionList.available)}
                                removeTab={removeTab}
                            >
                                {tabContent}
                            </Tabs>
                        </div>
                    </div>
                    <div className="button-group">
                        <button
                            ref={saveButtonRef}
                            disabled={disableSave}
                            onClick={() => {
                                save();
                            }}
                            className="button -primary -right"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default MeasurementsInput;
